/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import sideBarMenu from './Sidebar/menu';
import SidebarComponent from './Sidebar';
import { getPriceAction } from '../Overview/store/overviewActions';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../redux/store';
import HeaderDropdown from './HeaderDropdown';
import { Modal, Spin } from 'antd';
import FirstBranchIndex from '../Settings/FirstBranch';
import NoActiveBranchIndex from '../Settings/NoActiveBranch';
import { refreshStoredBranches } from '../Settings/addBranchApi';
// import Cookies from 'js-cookie';
// import { TOKEN } from '../../constant';

// The session is stale right after login for a brand-new organization: branches is []
const readHasBranch = (): boolean => {
  try {
    const stored = window.localStorage.getItem('branches');
    const parsed: unknown = stored === null ? [] : JSON.parse(stored);
    if (Array.isArray(parsed) && parsed.length > 0) return true;
    // every branch may be deactivated: the organization still has branches
    return Number(window.localStorage.getItem('branchCount') ?? 0) > 0;
  } catch {
    return false;
  }
};

// The pages read the branch dropdowns from 'branches', which only holds ACTIVE branches.
// With none, those pages would crash (they take the first branch), so they are not rendered.
const readHasActiveBranch = (): boolean => {
  try {
    const stored = window.localStorage.getItem('branches');
    const parsed: unknown = stored === null ? [] : JSON.parse(stored);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
};

export default function ProtectedLayout(): JSX.Element {
  const [hasBranch, setHasBranch] = useState<boolean>(readHasBranch);
  // re-read on every render (navigation) so activating / deactivating a branch in Settings is picked up
  const hasActiveBranch = readHasActiveBranch();
  // On a refresh with no branch saved, ask the server before showing the first-branch form
  const [isCheckingBranch, setIsCheckingBranch] = useState<boolean>(
    () => !readHasBranch() && window.localStorage.getItem('token') !== null
  );
  const [collapsed, setCollapsed] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  const orgName = localStorage.getItem('organizationName') ?? '';

  const isPaymentPending = orgName === '';

  const allowedRoles: any = {
    Overview: ['manager', 'auditor', 'superadmin', 'cashier', 'billing'],
    'Bank Pledge': ['manager', 'cashier', 'superadmin', 'approver'],
    Loan: ['manager', 'billing', 'superadmin', 'cashier', 'approver'],
    Customer: ['manager', 'billing', 'superadmin', 'cashier'],
    Transactions: ['manager', 'cashier', 'superadmin'],
    Deposits: ['manager', 'superadmin', 'cashier'],
    'Interest Scheme': ['superadmin'],
    Users: ['superadmin'],
    Investment: ['superadmin'],
    Recovery: ['superadmin', 'manager', 'billing', 'recovery'],
    Terms: ['manager', 'auditor', 'superadmin', 'cashier', 'billing'],
    'Bank Guide': ['manager', 'auditor', 'superadmin', 'cashier', 'billing'],
    Reports: ['superadmin', 'auditor'],
    'Cash Bank Book': ['cashier'],
    Sale: ['manager', 'billing', 'superadmin', 'cashier', 'approver'],
    Settings: ['superadmin'],
  };

  const [roleName, setRoleName] = useState<string>('auditor');

  const logout = () => {
    window.localStorage.clear();
    navigate('/sign-in');
  };

  const { goldPrice, silverPrice, saleGoldPrice, saleSilverPrice }: any =
    useSelector<any>((state) => state.overview);

  const dispatch = useDispatch<AppDispatch>();

  const getPrices = async (): Promise<void> => {
    await getPriceAction(dispatch);
  };

  useEffect(() => {
    const token = window.localStorage.getItem('token');
    if (token == null) {
      navigate('/sign-in');
    } else {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      const role = window.localStorage.getItem('role') ?? 'Auditor';
      setRoleName(role);

      const pathLabel = sideBarMenu.filter((x) => x.to === location.pathname)[0]
        .label;

      const locationEnabledRoles = allowedRoles[pathLabel];
      if (locationEnabledRoles.includes(role) === false) {
        navigate('/sign-in');
      }
    }

    // Branch-scoped data is only loaded once a branch exists
    if (hasBranch) void getPrices();
  }, []);

  // No branch saved in this session: check GET /branches (it may have been added since login)
  useEffect(() => {
    if (!isCheckingBranch) return;
    void (async () => {
      try {
        const branches = await refreshStoredBranches();
        if (branches.length > 0) {
          const current = window.localStorage.getItem('branch');
          if (current === null || current === 'null') {
            window.localStorage.setItem(
              'branch',
              JSON.stringify({ ...branches[0], id: branches[0]._id })
            );
          }
          setHasBranch(true);
          void getPrices();
        }
      } catch (error: any) {
        if (error?.response?.status === 401) {
          window.localStorage.clear();
          navigate('/sign-in');
        }
        // any other error: fall back to the first-branch form
      } finally {
        setIsCheckingBranch(false);
      }
    })();
  }, []);

  useEffect(() => {}, [goldPrice, silverPrice, saleGoldPrice, saleSilverPrice]);

  const userName: string | null = localStorage.getItem('username');

  const onFirstBranchAdded = () => {
    setHasBranch(true);
    navigate('/');
    void getPrices();
  };

  return (
    <div className="protected__layout__container">
      {hasBranch && (
        <div className="sidebar">
          <SidebarComponent
            collapsed={collapsed}
            role={roleName}
            toggleSidebar={toggleSidebar}
          />
        </div>
      )}
      <div className="main__container">
        {/* no header while the first-branch setup popup is shown */}
        {hasBranch && (
          <div
            className={
              collapsed
                ? 'header__container header__collapsed'
                : 'header__container header__expanded'
            }
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 64,
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 10,
                }}
              >
                <p style={{ margin: 0 }}>
                  <span style={{ color: goldPrice === 0 ? 'red' : '' }}>
                    Gold rate today: {goldPrice}
                  </span>
                  <span style={{ color: !saleGoldPrice ? 'red' : '' }}>
                    {' '}| Sale: {saleGoldPrice ?? 0}
                  </span>
                </p>
                <p style={{ margin: 0 }}>
                  <span style={{ color: silverPrice === 0 ? 'red' : '' }}>
                    Silver rate today: {silverPrice}
                  </span>
                  <span style={{ color: !saleSilverPrice ? 'red' : '' }}>
                    {' '}| Sale: {saleSilverPrice ?? 0}
                  </span>
                </p>
              </div>
              <HeaderDropdown username={userName ?? ''} logout={logout} />
            </div>
          </div>
        )}

        <div
          className={
            !hasBranch
              ? 'sidebar__none'
              : collapsed
                ? 'sidebar__collapsed'
                : 'sidebar__expanded'
          }
        >
          {isPaymentPending ? (
            <Modal
              open={true}
              footer={null}
              closable={false}
              centered
              className="full-screen-modal"
            >
              <h1 style={{ color: 'black', textAlign: 'center' }}>
                Server & Database Payment Pending
              </h1>
            </Modal>
          ) : isCheckingBranch ? (
            <Spin spinning fullscreen />
          ) : hasBranch && !hasActiveBranch && !location.pathname.startsWith('/settings') ? (
            <NoActiveBranchIndex isSuperAdmin={roleName === 'superadmin'} />
          ) : hasBranch ? (
            <Outlet />
          ) : (
            <FirstBranchIndex onDone={onFirstBranchAdded} />
          )}
        </div>
      </div>
    </div>
  );
}
